import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import { WashingMachine } from "lucide-react";
import LiveSignal from "@/components/LiveSignal";
import TwinSilhouette from "@/components/TwinSilhouette";
import { EinsteinIcon } from "@/components/FlowIcons";

/* Node metadata · each has hover / click tooltip content */
const INPUTS = [
  {
    id: "band",
    label: "INPUT 01: YOUR TRIKARMA BAND",
    x: 17,
    y: 18,
    at: 0.115,
    desc: "HRV • HEART RATE • SLEEP • STRESS • MOVEMENT • MORE",
    tipTitle: "TRIKARMA BAND",
    tip:
      "The connection to your body across the day and night. What your heart, breath and sleep are doing while everything else is happening.",
  },
  {
    id: "life",
    label: "INPUT 02: LIFESTYLE",
    x: 24,
    y: 38,
    at: 0.22,
    desc: "MEALS • HYDRATION • ROUTINES • MEDITATION • BREATHWORK • GOALS • YOUR FEEDBACK",
    tipTitle: "LIFESTYLE",
    tip: "What you tell Trifit. Meals, hydration, routines, meditation, breathwork, goals, your feedback.",
  },
  {
    id: "app",
    label: "INPUT 03: APP BEHAVIOUR",
    x: 15,
    y: 59,
    at: 0.325,
    desc: "COMPLETED • SKIPPED • CHANGED • ASKED",
    tipTitle: "APP BEHAVIOUR",
    tip: "What you tap, what you skip, what you change. The choices the sensors can&apos;t see from the outside.",
  },
  {
    id: "ctx",
    label: "INPUT 04: CONTEXT",
    x: 21,
    y: 79,
    at: 0.43,
    desc: "SCHEDULE • TRAVEL • ENVIRONMENT • AVAILABILITY",
    tipTitle: "CONTEXT",
    tip: "The world around the signal. A red-eye flight, a first meeting, a night out. The twin models you inside your real week.",
  },
];

const MODELS = [
  {
    name: "MAD SCIENTIST",
    role: "ANALYSIS & EXPERIMENTATION",
    Icon: EinsteinIcon,
  },
  { name: "WASHING MACHINE", role: "DATA CLEANING", Icon: WashingMachine },
];

const TIPS = {
  you: {
    title: "YOU",
    body: "One person. One bounded system. Everything the twin knows begins here.",
  },
  intel: {
    title: "TRIFIT INTELLIGENCE",
    body:
      "The layer that turns signals into a working model of you. Signals get cleaned. Patterns get interpreted. Evidence accumulates.",
  },
  twin: {
    title: "YOUR DIGITAL TWIN",
    body:
      "A model of you that grows with evidence. Each new observation tests what the twin thought it knew and sharpens what it does next.",
  },
  plan: {
    title: "YOUR PLAN",
    body:
      "What the twin currently believes will move your goal, shaped for the version of you it has been watching.",
  },
};

/* ------------------------------------------------------------------- */
/* Tooltip                                                              */
/* ------------------------------------------------------------------- */
const Tooltip = ({ title, body, side = "right", pinned }) => {
  const arrow =
    side === "right"
      ? "left-0 -translate-x-1/2"
      : side === "left"
      ? "right-0 translate-x-1/2"
      : side === "bottom"
      ? "left-1/2 -translate-x-1/2 top-0 -translate-y-1/2"
      : "left-1/2 -translate-x-1/2 bottom-0 translate-y-1/2";
  const posClass =
    side === "right"
      ? "left-full ml-3 top-1/2 -translate-y-1/2"
      : side === "left"
      ? "right-full mr-3 top-1/2 -translate-y-1/2"
      : side === "bottom"
      ? "left-1/2 -translate-x-1/2 top-full mt-3"
      : "left-1/2 -translate-x-1/2 bottom-full mb-3";

  return (
    <motion.div
      role="tooltip"
      initial={{ opacity: 0, y: 6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.98 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className={`pointer-events-none absolute z-30 w-[min(320px,72vw)] rounded-lg border ${
        pinned ? "border-signal/60" : "border-line"
      } bg-[#0C060A]/95 p-4 shadow-[0_8px_32px_rgba(0,0,0,0.6)] backdrop-blur-sm ${posClass}`}
      data-testid="arch-tooltip"
    >
      <span
        aria-hidden="true"
        className={`absolute h-2 w-2 rotate-45 border ${
          pinned ? "border-signal/60" : "border-line"
        } bg-[#0C060A] ${
          side === "right"
            ? "left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 border-r-0 border-t-0"
            : side === "left"
            ? "right-0 top-1/2 translate-x-1/2 -translate-y-1/2 border-b-0 border-l-0"
            : side === "bottom"
            ? "left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 border-b-0 border-r-0"
            : "left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 border-l-0 border-t-0"
        } ${arrow ? "" : ""}`}
      />
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-[9px] tracking-[0.24em] text-signal">
          {title}
        </p>
        {pinned && (
          <span className="font-mono text-[8px] tracking-[0.24em] text-ash">
            PINNED · CLICK TO CLOSE
          </span>
        )}
      </div>
      <p className="mt-2 text-xs leading-relaxed text-fog md:text-sm">{body}</p>
    </motion.div>
  );
};

/* ------------------------------------------------------------------- */
/* Architecture section                                                 */
/* ------------------------------------------------------------------- */
const Architecture = () => {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const [stateIdx, setStateIdx] = useState(1);
  useMotionValueEvent(p, "change", (v) =>
    setStateIdx(Math.min(8, Math.max(1, Math.floor(v * 8.4) + 1)))
  );

  /* Tooltip state · hovered wins unless something is pinned */
  const [hoverId, setHoverId] = useState(null);
  const [pinnedId, setPinnedId] = useState(null);
  const activeId = pinnedId || hoverId;

  // Unpin when user scrolls out of this section
  useEffect(() => {
    const unsub = p.on("change", (v) => {
      if (v <= 0 || v >= 1) setPinnedId(null);
    });
    return () => unsub();
  }, [p]);

  const handleNode = (id) => (e) => {
    e.stopPropagation();
    setPinnedId((cur) => {
      const next = cur === id ? null : id;
      if (next === null) setHoverId(null);
      return next;
    });
  };
  const handleHoverIn = (id) => () => setHoverId(id);
  const handleHoverOut = () => setHoverId(null);

  const headOpacity = useTransform(p, [0, 0.03, 0.06, 0.1], [0, 1, 1, 0]);
  const youOpacity = useTransform(p, [0, 0.04, 0.6, 0.7], [0, 1, 1, 0.25]);
  const intelOpacity = useTransform(p, [0.53, 0.6], [0, 1]);
  const modelsOpacity = useTransform(p, [0.63, 0.7], [0, 1]);
  const twinOpacity = useTransform(p, [0.73, 0.8], [0, 1]);
  const planOpacity = useTransform(p, [0.8, 0.87], [0, 1]);
  const lineYou = useTransform(p, [0.06, 0.45], [0, 1]);
  const lineIn = useTransform(p, [0.5, 0.58], [0, 1]);
  const lineTwin = useTransform(p, [0.72, 0.8], [0, 1]);
  const linePlan = useTransform(p, [0.8, 0.87], [0, 1]);
  const lineLoop = useTransform(p, [0.87, 0.95], [0, 1]);
  const barScale = p;

  return (
    <section
      ref={ref}
      data-testid="architecture-section"
      className="relative h-[500vh]"
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* header */}
        <motion.div
          style={{ opacity: headOpacity }}
          className="absolute left-6 top-24 md:left-12"
        >
          <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
            02 · SYSTEM
          </p>
          <h2 className="mt-4 max-w-md font-display text-3xl font-light text-white">
            How Trifit builds your digital twin
          </h2>
        </motion.div>

        {/* progress */}
        <div className="absolute right-6 top-24 flex items-center gap-4 md:right-12">
          <p className="font-mono text-[10px] tracking-[0.2em] text-ash">
            STATE {String(stateIdx).padStart(2, "0")} / 08
          </p>
          <div className="h-px w-24 bg-line">
            <motion.div
              style={{ scaleX: barScale }}
              className="h-px origin-left bg-white/60"
            />
          </div>
        </div>

        <div className="absolute inset-0">
          {/* connection lines · svg */}
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {INPUTS.map((inp) => (
              <motion.path
                key={`you-${inp.id}`}
                d={`M 7 50 L ${inp.x} ${inp.y}`}
                stroke="#2A2026"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
                fill="none"
                style={{ pathLength: lineYou }}
              />
            ))}
            {INPUTS.map((inp) => (
              <motion.path
                key={`in-${inp.id}`}
                d={`M ${inp.x} ${inp.y} L 52 50`}
                stroke="#2A2026"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
                fill="none"
                style={{ pathLength: lineIn }}
              />
            ))}
            {INPUTS.map((inp) => (
              <motion.path
                key={`flow-${inp.id}`}
                d={`M ${inp.x} ${inp.y} L 52 50`}
                stroke="#A19A9D"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
                fill="none"
                className="signal-flow"
                style={{ opacity: lineIn }}
              />
            ))}
            {/* intelligence -> twin */}
            <motion.path d="M 52 50 L 81 28" stroke="#2A2026" strokeWidth="1" vectorEffect="non-scaling-stroke" fill="none" style={{ pathLength: lineTwin }} />
            <motion.path d="M 52 50 L 81 28" stroke="#D2378B" strokeWidth="1" vectorEffect="non-scaling-stroke" fill="none" className="signal-flow" style={{ opacity: lineTwin }} />
            {/* twin -> plan */}
            <motion.path d="M 81 28 L 81 72" stroke="#2A2026" strokeWidth="1" vectorEffect="non-scaling-stroke" fill="none" style={{ pathLength: linePlan }} />
            <motion.path d="M 81 28 L 81 72" stroke="#D2378B" strokeWidth="1" vectorEffect="non-scaling-stroke" fill="none" className="signal-flow" style={{ opacity: linePlan }} />
            {/* plan -> intelligence loop */}
            <motion.path d="M 81 72 Q 62 86 52 50" stroke="#2A2026" strokeWidth="1" vectorEffect="non-scaling-stroke" fill="none" style={{ pathLength: lineLoop }} />
            <motion.path d="M 81 72 Q 62 86 52 50" stroke="#D2378B" strokeWidth="1" vectorEffect="non-scaling-stroke" fill="none" className="signal-flow" style={{ opacity: lineLoop }} />
            {/* twin -> intelligence secondary loop */}
            <motion.path d="M 81 28 Q 62 12 52 50" stroke="#2A2026" strokeWidth="1" vectorEffect="non-scaling-stroke" fill="none" style={{ pathLength: lineLoop }} />
            <motion.path d="M 81 28 Q 62 12 52 50" stroke="#A19A9D" strokeWidth="1" vectorEffect="non-scaling-stroke" fill="none" className="signal-flow" style={{ opacity: lineLoop }} />
          </svg>

          {/* YOU */}
          <motion.div
            style={{ opacity: youOpacity, left: "7%", top: "50%" }}
            className="absolute z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer text-center"
            data-testid="arch-node-you"
            onMouseEnter={handleHoverIn("you")}
            onMouseLeave={handleHoverOut}
            onClick={handleNode("you")}
          >
            <div className="relative mx-auto flex h-24 w-24 items-center justify-center rounded-full border border-white/30 bg-[#080205]">
              <span className="pulse-ring absolute inset-0 rounded-full border border-white/40" />
              <div className="h-2 w-2 rounded-full bg-white" />
            </div>
            <p className="mt-4 font-mono text-[12px] tracking-[0.24em] text-white">
              YOU
            </p>
            {activeId === "you" && (
              <Tooltip
                title={TIPS.you.title}
                body={TIPS.you.body}
                side="right"
                pinned={pinnedId === "you"}
              />
            )}
          </motion.div>

          {/* inputs */}
          {INPUTS.map((inp) => (
            <InputNode
              key={inp.id}
              p={p}
              inp={inp}
              active={activeId === inp.id}
              pinned={pinnedId === inp.id}
              onEnter={handleHoverIn(inp.id)}
              onLeave={handleHoverOut}
              onClick={handleNode(inp.id)}
            />
          ))}

          {/* Trifit Intelligence */}
          <motion.div
            style={{
              opacity: intelOpacity,
              left: "52%",
              top: "50%",
              width: "clamp(320px, 30%, 460px)",
            }}
            className="absolute z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-xl border border-white/25 bg-[#0C060A] p-7"
            data-testid="arch-node-intel"
            onMouseEnter={handleHoverIn("intel")}
            onMouseLeave={handleHoverOut}
            onClick={handleNode("intel")}
          >
            <p className="font-mono text-[12px] leading-relaxed tracking-[0.24em] text-white">
              TRIFIT INTELLIGENCE
            </p>
            <div className="tech-rule mt-3 w-full" />
            <motion.div
              style={{ opacity: modelsOpacity }}
              className="mt-5 grid grid-cols-2 gap-3"
            >
              {MODELS.map(({ name, role, Icon }) => (
                <div
                  key={name}
                  data-testid={`ml-chip-${name.split(" ")[0].toLowerCase()}`}
                  className="flex flex-col items-center justify-start rounded-md border border-line px-2 py-5 text-center"
                >
                  <Icon
                    className="mb-2 h-5 w-5 shrink-0 text-signal"
                    strokeWidth={1.5}
                  />
                  <p className="font-mono text-[11px] leading-relaxed tracking-[0.14em] text-fog">
                    {name}
                  </p>
                  <p className="mt-1 font-mono text-[10px] leading-relaxed tracking-[0.1em] text-ash">
                    {role}
                  </p>
                </div>
              ))}
            </motion.div>
            <p className="mt-4 font-mono text-[10px] leading-relaxed tracking-[0.18em] text-ash">
              MODEL: CONTINUOUS LEARNING
            </p>
            {activeId === "intel" && (
              <Tooltip
                title={TIPS.intel.title}
                body={TIPS.intel.body}
                side="top"
                pinned={pinnedId === "intel"}
              />
            )}
          </motion.div>

          {/* twin */}
          <motion.div
            style={{
              opacity: twinOpacity,
              left: "81%",
              top: "28%",
              width: "clamp(200px, 19%, 290px)",
            }}
            className="absolute z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            data-testid="arch-node-twin"
            onMouseEnter={handleHoverIn("twin")}
            onMouseLeave={handleHoverOut}
            onClick={handleNode("twin")}
          >
            <div className="overflow-hidden rounded-lg border border-line bg-[#0C060A] p-4">
              <TwinSilhouette className="h-56 w-full" />
              <p className="mt-3 text-center font-mono text-[11px] leading-relaxed tracking-[0.2em] text-white">
                YOUR DIGITAL TWIN
              </p>
              <LiveSignal className="mt-2 h-5 w-full" />
            </div>
            {activeId === "twin" && (
              <Tooltip
                title={TIPS.twin.title}
                body={TIPS.twin.body}
                side="left"
                pinned={pinnedId === "twin"}
              />
            )}
          </motion.div>

          {/* plan */}
          <motion.div
            style={{
              opacity: planOpacity,
              left: "81%",
              top: "72%",
              width: "clamp(180px, 18%, 280px)",
            }}
            className="absolute z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-md border border-white/30 bg-[#0C060A] px-5 py-7 text-center"
            data-testid="arch-node-plan"
            onMouseEnter={handleHoverIn("plan")}
            onMouseLeave={handleHoverOut}
            onClick={handleNode("plan")}
          >
            <p className="font-mono text-[12px] leading-relaxed tracking-[0.2em] text-white">
              YOUR PLAN
            </p>
            <p className="mt-2 font-mono text-[10px] leading-relaxed tracking-[0.12em] text-signal/80">
              OUTPUT: ADAPTIVE
            </p>
            {activeId === "plan" && (
              <Tooltip
                title={TIPS.plan.title}
                body={TIPS.plan.body}
                side="left"
                pinned={pinnedId === "plan"}
              />
            )}
          </motion.div>
        </div>

        {/* interaction hint */}
        <div className="pointer-events-none absolute bottom-8 left-6 md:left-12">
          <p className="font-mono text-[9px] tracking-[0.24em] text-ash">
            HOVER A NODE · CLICK TO PIN
          </p>
        </div>
      </div>
    </section>
  );
};

const InputNode = ({ p, inp, active, pinned, onEnter, onLeave, onClick }) => {
  const opacity = useTransform(p, [inp.at, inp.at + 0.03], [0, 1]);
  return (
    <motion.div
      style={{
        opacity,
        left: `${inp.x}%`,
        top: `${inp.y}%`,
        width: "clamp(240px, 26%, 380px)",
      }}
      className="absolute z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-md border border-line bg-[#0C060A] px-5 py-4"
      data-testid={`arch-node-${inp.id}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={onClick}
    >
      <p className="font-mono text-[11px] leading-relaxed tracking-[0.16em] text-white">
        {inp.label}
      </p>
      <p className="mt-2 font-mono text-[10px] leading-relaxed tracking-[0.1em] text-ash">
        {inp.desc}
      </p>
      {active && (
        <Tooltip
          title={inp.tipTitle}
          body={inp.tip}
          side="right"
          pinned={pinned}
        />
      )}
    </motion.div>
  );
};

export default Architecture;
